import Keycloak, { type KeycloakServerConfig, type KeycloakTokenParsed } from 'keycloak-js';

import {
  assertArrayOfNonEmptyStrings,
  assertEmptyOrString,
  assertNonEmptyString,
  assertRecord,
} from '@/core/lib/AssertUtils';
import { getConfig, type SoaConfig } from '@/core/lib/Config';
import { createKeycloak } from '@/domains/authentification/lib/KeycloakFactory';
import { captureError } from '@/domains/logger/lib/ApmUtils';

export type Config = KeycloakServerConfig & { debug?: boolean };

export enum Reseau {
  ALPTIS = 'ALPTIS',
  MILTIS = 'MILTIS',
}

export interface Distributeur {
  id: string;
  role: string;
  scopes: string[];
}

export interface LoggedInUser {
  id: string;
  isImpersonated: boolean;
  /**
   * Devra être déprécié dans le future, avec la disparition RSI
   */
  codeDistributeur?: string;
  reseau?: string;
  distributeur: Distributeur;
}

export type AuthenticationState = Pick<Keycloak, 'authenticated' | 'token' | 'tokenParsed' | 'idToken'> & {
  user: LoggedInUser | undefined;
  isTokenRefreshed: boolean;
  logoutUrl: string | undefined;
};

export type EventType = 'success' | 'error' | 'tokenExpired' | 'refreshSuccess' | 'refreshError' | 'logout';

export type Listener = (state: AuthenticationState, eventType: EventType, ...args: unknown[]) => void | Promise<void>;

export interface Authentication {
  readonly authenticate: () => Promise<AuthenticationState>;
  readonly updateToken: () => Promise<void>;
  readonly getState: () => AuthenticationState;
}

let authenticationPromise: Promise<Authentication> | undefined;

export class AuthenticationError extends Error {}

interface TokenParsed extends KeycloakTokenParsed {
  code_distributeur: string | undefined;
  reseau: string | undefined;
  compte: {
    id: string;
  };
  utilisateur: {
    id: string;
  };
  distributeur: {
    id: string;
    role: string;
  };
  scopes: string[];
}

function assertTokenParsed(tokenParsed: KeycloakTokenParsed | undefined): asserts tokenParsed is TokenParsed {
  assertRecord(tokenParsed, 'tokenParsed');

  assertEmptyOrString(tokenParsed.code_distributeur, 'tokenParsed.code_distributeur');
  assertEmptyOrString(tokenParsed.reseau, 'tokenParsed.reseau');

  assertRecord(tokenParsed.utilisateur, 'tokenParsed.utilisateur');
  assertNonEmptyString(tokenParsed.utilisateur.id, 'tokenParsed.utilisateur.id');

  assertRecord(tokenParsed.compte, 'tokenParsed.compte');
  assertNonEmptyString(tokenParsed.compte.id, 'tokenParsed.compte.id');

  assertRecord(tokenParsed.distributeur, 'tokenParsed.distributeur');
  assertNonEmptyString(tokenParsed.distributeur.id, 'tokenParsed.distributeur.id');
  assertNonEmptyString(tokenParsed.distributeur.role, 'tokenParsed.distributeur.role');

  assertArrayOfNonEmptyStrings(tokenParsed.scopes, 'tokenParsed.scopes');
}

function mapStateFromKeycloak(soaConfig: SoaConfig, keycloak: Keycloak): AuthenticationState {
  const tokenParsed = keycloak.tokenParsed;

  let user: LoggedInUser | undefined;
  let logoutUrl: string | undefined = undefined;

  if (keycloak.authenticated) {
    logoutUrl = getLogoutUrl(soaConfig, keycloak.idToken);

    try {
      assertTokenParsed(tokenParsed);

      user = {
        id: tokenParsed.utilisateur.id,
        isImpersonated: tokenParsed.compte.id !== tokenParsed.utilisateur.id,
        codeDistributeur: tokenParsed.code_distributeur,
        reseau: tokenParsed.reseau,
        distributeur: {
          id: tokenParsed.distributeur.id,
          scopes: tokenParsed.scopes,
          role: tokenParsed.distributeur.role,
        },
      };
    } catch (e) {
      console.error('An error occurred while mapping this token parsed :', tokenParsed);

      console.error(e, logoutUrl);
      window.open(logoutUrl, '_self');

      throw e;
    }
  }

  return {
    authenticated: keycloak.authenticated,
    token: keycloak.token,
    tokenParsed: keycloak.tokenParsed,
    user,
    isTokenRefreshed: false,
    logoutUrl,
  };
}

let isUpdatingToken = false;

async function updateToken(keycloak: Keycloak) {
  const tokenLifespan = 60;

  if (!isUpdatingToken) {
    isUpdatingToken = true;

    await keycloak.updateToken(tokenLifespan).finally(() => {
      isUpdatingToken = false;
    });
  }
}

function getLogoutUrl(soaConfig: SoaConfig, idToken: string | undefined): string {
  const {
    espaceDigitalHomeUrl,
    authentication: { realm, url: authenticationUrl },
  } = soaConfig;

  const logoutEspaceProQueryParams = `auth_url_return=${encodeURIComponent(espaceDigitalHomeUrl)}`;
  const logoutEspaceProUrl = `${espaceDigitalHomeUrl}/index.php/auth/log/out?${logoutEspaceProQueryParams}`;

  const logoutUrlQueryParams = `id_token_hint=${idToken?.toString() ?? ''}&post_logout_redirect_uri=${encodeURIComponent(
    import.meta.env.DEV ? 'http://localhost:8080' : logoutEspaceProUrl,
  )}`;

  return `${authenticationUrl}/realms/${realm}/protocol/openid-connect/logout?${logoutUrlQueryParams}`;
}

export function logout(state: AuthenticationState) {
  window.open(state.logoutUrl, '_self');
}

function createAuthentication(soaConfig: SoaConfig, keycloak: Keycloak): Authentication {
  const getState = (): AuthenticationState => mapStateFromKeycloak(soaConfig, keycloak);

  return {
    getState,
    authenticate: async (): Promise<AuthenticationState> => {
      if (!keycloak.authenticated) {
        await keycloak.login();
      }

      return getState();
    },
    updateToken: () => updateToken(keycloak),
  };
}

function getDebugListener(debug: boolean): Listener {
  return (state, eventType, error) => {
    if (error) {
      console.error(`error during ${eventType}`, error, state);
      captureError(error);
    } else if (debug) {
      console.log(`authentication: ${eventType}`, state);
    }
  };
}

export function listenFor(eventTypes: EventType[], listener: Listener) {
  return async (state: AuthenticationState, currentEventType: EventType, ...args: unknown[]): Promise<void> => {
    if (eventTypes.includes(currentEventType)) {
      return listener(state, currentEventType, ...args);
    }
  };
}

function getLogoutOnRefreshErrorListener() {
  return listenFor(['refreshError'], logout);
}

const AUTHENTICATION_INIT_TIMEOUT_MS = 15_000;

function createTimeout(timeoutMs: number, timeoutMessage: string): { promise: Promise<never>; clear: () => void } {
  let timeoutId: ReturnType<typeof setTimeout>;

  const promise = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(new AuthenticationError(timeoutMessage));
    }, timeoutMs);
  });

  return { promise, clear: () => {
    clearTimeout(timeoutId);
  } };
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  const timeout = createTimeout(timeoutMs, timeoutMessage);

  try {
    return await Promise.race([promise, timeout.promise]);
  } finally {
    timeout.clear();
  }
}

export async function initAuthentication(
  { url, realm, clientId, debug = false }: Config,
  authListeners: Listener[] = [],
): Promise<void> {
  authenticationPromise = withTimeout(
    (async () => {
      const soaConfig = await getConfig();

      const keycloak = await createKeycloak({ url, realm, clientId });

      const allAuthListeners: Listener[] = [
        getDebugListener(debug),
        getLogoutOnRefreshErrorListener(),
        ...authListeners,
      ];

      const callListeners = (eventType: EventType, args: unknown[]) => {
        const mappedStateFromKeycloak: AuthenticationState = mapStateFromKeycloak(soaConfig, keycloak);

        return Promise.all(
          allAuthListeners
            .map(listener => listener(mappedStateFromKeycloak, eventType, ...args))
            .filter(listener => listener instanceof Promise),
        );
      };

      Object.assign(keycloak, {
        onAuthSuccess: (...args: unknown[]) => callListeners('success', args),
        onAuthError: (...args: unknown[]) => callListeners('error', args),
        onAuthRefreshSuccess: (...args: unknown[]) => callListeners('refreshSuccess', args),
        onAuthRefreshError: (...args: unknown[]) => callListeners('refreshError', args),
        onAuthLogout: (...args: unknown[]) => callListeners('logout', args),
        onTokenExpired: (...args: unknown[]) => callListeners('tokenExpired', args),
      });

      await keycloak.init({ responseMode: 'query', checkLoginIframe: false, enableLogging: debug });

      return createAuthentication(soaConfig, keycloak);
    })(),
    AUTHENTICATION_INIT_TIMEOUT_MS,
    `L'initialisation de l'authentification a dépassé le délai de ${AUTHENTICATION_INIT_TIMEOUT_MS.toString()}ms.`,
  );

  await authenticationPromise;
}

export async function getAuthentication(): Promise<Authentication> {
  const authentication = await authenticationPromise;

  if (!authentication) {
    throw new AuthenticationError("Aucune instance d'authentification trouvée.");
  }

  return authentication;
}

export async function getLoggedInUser(): Promise<LoggedInUser> {
  const user = (await getAuthentication()).getState().user;

  if (user === undefined) {
    throw new Error('Utilisateur authentifié introuvable.');
  }

  return user;
}

export async function isAuthenticationFresh(): Promise<boolean> {
  const { getState } = await getAuthentication();

  const { authenticated, tokenParsed } = getState();

  if (authenticated) {
    const FRESH_AUTHENTICATION_DELAY = 3;

    if (tokenParsed === undefined) {
      throw new AuthenticationError('Token parsed non trouvé.');
    }

    if (tokenParsed.iat === undefined) {
      throw new AuthenticationError('iat non trouvé dans token parsed.');
    }

    return (Date.now() / 1000) - tokenParsed.iat <= FRESH_AUTHENTICATION_DELAY;
  }

  return false;
}

export async function setTokenAsRefreshed(): Promise<void> {
  (await getAuthentication()).getState().isTokenRefreshed = true;
}

export async function isTokenRefreshed(): Promise<boolean> {
  return (await getAuthentication()).getState().isTokenRefreshed;
}
